import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Search, Check, Type, Palette, Layout, Image, Eye, Save, Lock, ChevronRight, AlertTriangle, RotateCcw, Download, Loader2, ImageIcon } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'layout', label: 'Layout', icon: Layout },
  { key: 'icon', label: 'Icon', icon: Image },
  { key: 'text', label: 'Text', icon: Type },
  { key: 'style', label: 'Style', icon: Palette },
  { key: 'review', label: 'Review', icon: Eye },
];

const COLOR_PRESETS = [
  '#1B3A5C', '#FFFFFF', '#4A90A4', '#F5A623', '#000000',
  '#C41E3A', '#2E7D32', '#6A1B9A', '#E65100', '#1565C0',
  '#795548', '#37474F', '#D4AF37', '#8B0000', '#004D40',
];

/* Match a decoration size to the best canvas preset */
function _matchCanvasPreset(size, presets) {
  // Direct size match: 2.5" → hat-front, 4" → left-chest, 10" → full-back etc.
  const sizeMap = {
    2.5: 'hat-front',
    3: 'sleeve',
    4: 'left-chest',
    6: 'left-chest',  // 6" fits in left-chest or full-chest area
    10: 'full-back',
    12: 'full-back',
  };
  const slug = sizeMap[size];
  if (slug) {
    const found = presets.find(p => p.slug === slug);
    if (found) return found;
  }
  // Fallback: find preset where width_inches >= size
  const sorted = [...presets].sort((a, b) => a.width_inches - b.width_inches);
  return sorted.find(p => p.width_inches >= size) || sorted[sorted.length - 1];
}

/* ── SVG Live Preview ── */
const DesignPreview = ({ design, icon, layout, font, canvasPreset, size = 300 }) => {
  const cfg = layout?.config || {};
  const prim = design.primaryColor || '#1B3A5C';
  const sec = design.secondaryColor || '#FFFFFF';
  const mainText = design.uppercase ? (design.mainText || '').toUpperCase() : (design.mainText || '');
  const subtext = design.uppercase ? (design.subtext || '').toUpperCase() : (design.subtext || '');
  const fontFamily = font?.family || 'Montserrat';

  // Canvas aspect ratio — determines the visible printable boundary
  const cp = canvasPreset;
  const aspect = cp ? cp.width_inches / cp.height_inches : 1;
  // Map canvas to SVG coordinate space (100x100 viewBox)
  // Canvas is centered and fills most of the viewBox
  const canvasMargin = 5;
  let cw, ch;
  if (aspect >= 1) {
    cw = 100 - canvasMargin * 2;
    ch = cw / aspect;
  } else {
    ch = 100 - canvasMargin * 2;
    cw = ch * aspect;
  }
  const cx = (100 - cw) / 2;
  const cy = (100 - ch) / 2;

  const renderIcon = (x, y, maxSize) => {
    if (!icon) return null;
    const svgContent = icon.svg_data
      .replace(/<svg[^>]*>/, '')
      .replace(/<\/svg>/, '')
      .replace(/currentColor/g, sec);
    const scale = maxSize / 64;
    return (
      <g transform={`translate(${x - maxSize/2}, ${y - maxSize/2}) scale(${scale})`}
         dangerouslySetInnerHTML={{ __html: svgContent }} />
    );
  };

  const renderBadge = () => {
    if (!cfg.badge) return null;
    const r = cfg.badgeRadius || 44;
    return (
      <>
        <circle cx="50" cy="50" r={r} fill="none" stroke={prim} strokeWidth="3" />
        <circle cx="50" cy="50" r={r - 4} fill="none" stroke={prim} strokeWidth="1" />
      </>
    );
  };

  const renderArcText = (text, textCfg, color, id) => {
    if (!text || !textCfg?.arc) return null;
    const r = textCfg.arcRadius || 36;
    const flip = textCfg.arcFlip;
    // Auto-fit: reduce font size if text is long for the arc
    let fontSize = textCfg.fontSize || 11;
    const maxArcLen = Math.PI * r;
    const estCharW = fontSize * 0.65;
    if (text.length * estCharW > maxArcLen) {
      fontSize = Math.max(maxArcLen / (text.length * 0.65), 5);
    }
    return (
      <>
        <defs>
          {flip ? (
            <path id={id} d={`M ${50 + r},50 A ${r},${r} 0 1,0 ${50 - r},50`} />
          ) : (
            <path id={id} d={`M ${50 - r},50 A ${r},${r} 0 1,1 ${50 + r},50`} />
          )}
        </defs>
        <text fill={color} fontSize={fontSize} fontFamily={fontFamily}
              fontWeight="bold" letterSpacing="2">
          <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">
            {text}
          </textPath>
        </text>
      </>
    );
  };

  const renderStraightText = (text, textCfg, color) => {
    if (!text || !textCfg) return null;
    // Auto-fit: scale font to fit within canvas width
    let fontSize = textCfg.fontSize || 14;
    const maxW = cp ? cw * 0.9 : (textCfg.maxWidth || 90);
    const estCharW = fontSize * 0.6;
    const textW = text.length * estCharW;
    if (textW > maxW) {
      fontSize = Math.max((maxW / text.length) / 0.6, 5);
    }
    return (
      <text x={textCfg.x} y={textCfg.y} textAnchor={textCfg.anchor || 'middle'}
            fill={color} fontSize={fontSize} fontFamily={fontFamily}
            fontWeight="bold">
        {text}
      </text>
    );
  };

  return (
    <svg viewBox="0 0 100 100" width={size} height={size}
         style={{ background: '#f8f8f8', borderRadius: 12 }}
         data-testid="design-preview-svg">
      {/* Canvas boundary */}
      {cp && (
        <>
          <rect x={cx} y={cy} width={cw} height={ch}
                fill="none" stroke="#4A90A4" strokeWidth="0.5" strokeDasharray="2,1.5" opacity="0.6" />
          <text x={cx + cw / 2} y={cy - 1.5} textAnchor="middle" fill="#4A90A4" fontSize="3" opacity="0.5">
            {cp.width_inches}" x {cp.height_inches}" — {cp.name}
          </text>
        </>
      )}

      {/* Background for badge layouts */}
      {cfg.badge && <circle cx="50" cy="50" r={cfg.badgeRadius || 44} fill={prim} opacity="0.08" />}
      {renderBadge()}

      {/* Icon */}
      {cfg.icon && design.iconId && renderIcon(cfg.icon.x, cfg.icon.y, cfg.icon.maxSize)}

      {/* Main text */}
      {cfg.mainText?.arc
        ? renderArcText(mainText, cfg.mainText, prim, 'arc-main')
        : renderStraightText(mainText, cfg.mainText, prim)}

      {/* Subtext */}
      {cfg.subtext?.arc
        ? renderArcText(subtext, cfg.subtext, prim, 'arc-sub')
        : renderStraightText(subtext, cfg.subtext, prim)}

      {/* Empty state */}
      {!mainText && !design.iconId && (
        <text x="50" y="52" textAnchor="middle" fill="#ccc" fontSize="6" fontFamily="sans-serif">
          Your design preview
        </text>
      )}
    </svg>
  );
};


/* ── Main Builder Page ── */
const DesignBuilderPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token } = useCustomerAuth();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');
  const [sessionStatus, setSessionStatus] = useState('draft');

  // Design state
  const [design, setDesign] = useState({
    canvasPresetId: '', layoutId: '', iconId: '', mainText: '', subtext: '',
    fontId: 'montserrat', primaryColor: '#1B3A5C', secondaryColor: '#FFFFFF',
    accentColor: '', uppercase: false, overflowApproved: false,
  });

  const [sessionId, setSessionId] = useState(null);

  // Library data
  const [icons, setIcons] = useState([]);
  const [iconCategories, setIconCategories] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [canvasPresets, setCanvasPresets] = useState([]);

  // UI state
  const [iconFilter, setIconFilter] = useState('');
  const [iconSearch, setIconSearch] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [assets, setAssets] = useState([]);
  const [staffNotes, setStaffNotes] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedPlacement, setSelectedPlacement] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const selectedIcon = useMemo(() => icons.find(i => i.id === design.iconId), [icons, design.iconId]);
  const selectedLayout = useMemo(() => layouts.find(l => l.slug === design.layoutId), [layouts, design.layoutId]);
  const selectedFont = useMemo(() => fonts.find(f => f.id === design.fontId), [fonts, design.fontId]);
  const selectedCanvas = useMemo(() => canvasPresets.find(c => c.id === design.canvasPresetId), [canvasPresets, design.canvasPresetId]);

  // Overflow detection: estimate if text exceeds canvas width
  const overflowDetected = useMemo(() => {
    if (!selectedCanvas || !selectedLayout) return false;
    const cfg = selectedLayout.config || {};
    const mainText = design.uppercase ? (design.mainText || '').toUpperCase() : (design.mainText || '');
    const subtext = design.uppercase ? (design.subtext || '').toUpperCase() : (design.subtext || '');
    const aspect = selectedCanvas.width_inches / selectedCanvas.height_inches;
    const canvasW = aspect >= 1 ? 90 : (90 / aspect > 90 ? 90 : 90 * aspect);

    const checkOverflow = (text, textCfg) => {
      if (!text || !textCfg || textCfg.arc) return false;
      const fontSize = textCfg.fontSize || 14;
      const estW = text.length * fontSize * 0.6;
      return estW > canvasW;
    };

    return checkOverflow(mainText, cfg.mainText) || checkOverflow(subtext, cfg.subtext);
  }, [design, selectedCanvas, selectedLayout]);

  // Load data
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [iconsRes, catsRes, layoutsRes, fontsRes, canvasRes, sessionRes] = await Promise.all([
          axios.get(`${API}/api/design/icons`, { headers }),
          axios.get(`${API}/api/design/icon-categories`, { headers }),
          axios.get(`${API}/api/design/layouts`, { headers }),
          axios.get(`${API}/api/design/fonts`, { headers }),
          axios.get(`${API}/api/design/canvas-presets`, { headers }),
          axios.get(`${API}/api/design/order/${orderId}`, { headers }),
        ]);
        setIcons(iconsRes.data);
        setIconCategories(catsRes.data);
        setLayouts(layoutsRes.data);
        setFonts(fontsRes.data);
        setCanvasPresets(canvasRes.data);
        setEligible(sessionRes.data.eligible);
        setOrderStatus(sessionRes.data.order_status);
        
        // Store order items for placement-based canvas selection
        const items = sessionRes.data.order_items || [];
        setOrderItems(items);

        if (sessionRes.data.design_session) {
          const s = sessionRes.data.design_session;
          setSessionId(s.id);
          setSessionStatus(s.status);
          setDesign({
            canvasPresetId: s.canvas_preset_id || '',
            layoutId: s.layout_template_id || '',
            iconId: s.icon_id || '',
            mainText: s.main_text || '',
            subtext: s.subtext || '',
            fontId: s.font_id || 'montserrat',
            primaryColor: s.primary_color || '#1B3A5C',
            secondaryColor: s.secondary_color || '#FFFFFF',
            accentColor: s.accent_color || '',
            uppercase: s.uppercase || false,
            overflowApproved: s.overflow_approved || false,
          });
          if (s.validation_results?.warnings) setWarnings(s.validation_results.warnings);
          if (s.validation_results?.staff_notes) setStaffNotes(s.validation_results.staff_notes);
        } else {
          // Auto-select canvas from first order item placement if no existing session
          const presets = canvasRes.data;
          if (items.length > 0 && presets.length > 0) {
            const firstItem = items[0];
            const opts = firstItem.customization_options || {};
            const decoSize = opts.decoration_size;
            const complexity = opts.decoration_complexity;
            // Try to match a canvas preset by size
            if (decoSize) {
              const matched = _matchCanvasPreset(decoSize, presets);
              if (matched) {
                setDesign(prev => ({ ...prev, canvasPresetId: matched.id }));
                setSelectedPlacement({ item: firstItem, size: decoSize });
              }
            }
          }
        }
      } catch (err) {
        console.error('Load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, orderId, headers]);

  // Auto-save on design change (debounced)
  const saveDesign = useCallback(async (d) => {
    if (!token || !eligible) return;
    if (sessionStatus !== 'draft' && sessionStatus !== 'revisions_requested') return;
    try {
      const res = await axios.post(`${API}/api/design/order/${orderId}`, {
        canvas_preset_id: d.canvasPresetId || null,
        layout_template_id: d.layoutId || null,
        icon_id: d.iconId || null,
        main_text: d.mainText || null,
        subtext: d.subtext || null,
        font_id: d.fontId || null,
        primary_color: d.primaryColor || null,
        secondary_color: d.secondaryColor || null,
        accent_color: d.accentColor || null,
        uppercase: d.uppercase,
        overflow_approved: d.overflowApproved || false,
      }, { headers });
      if (res.data.id) setSessionId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
    }
  }, [token, orderId, eligible, sessionStatus, headers]);

  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => saveDesign(design), 800);
    return () => clearTimeout(timer);
  }, [design, loading, saveDesign]);

  const updateDesign = (field, value) => {
    setDesign(prev => ({ ...prev, [field]: value }));
  };

  const handleFinalize = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/design/order/${orderId}/finalize`, {}, { headers });
      setSessionStatus('finalized_by_customer');
      setWarnings(res.data.warnings || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to finalize');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    try {
      await axios.put(`${API}/api/design/order/${orderId}/unlock`, {}, { headers });
      setSessionStatus('draft');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to unlock');
    }
  };

  const handleRenderPNG = async () => {
    setRendering(true);
    try {
      const res = await axios.post(`${API}/api/design/order/${orderId}/render-png`, {}, { headers });
      setAssets(prev => [
        { type: 'flat_design', url: res.data.flat_design_url, label: 'Flat Design' },
        { type: 'embroidery_preview', url: res.data.embroidery_preview_url, label: 'Embroidery Preview' },
        ...prev.filter(a => a.type !== 'flat_design' && a.type !== 'embroidery_preview'),
      ]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to render');
    } finally {
      setRendering(false);
    }
  };

  const handleRenderMockup = async () => {
    setRendering(true);
    try {
      const res = await axios.post(`${API}/api/design/order/${orderId}/render-mockup`, {}, { headers });
      setAssets(prev => [
        { type: 'garment_mockup', url: res.data.mockup_url, label: `Garment Mockup — ${res.data.placement}` },
        ...prev.filter(a => a.type !== 'garment_mockup'),
      ]);
    } catch (err) {
      alert(err.response?.data?.detail || 'No mockup templates available yet. Ask admin to upload garment images.');
    } finally {
      setRendering(false);
    }
  };

  const loadAssets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/design/order/${orderId}/assets`, { headers });
      if (res.data.assets?.length) {
        setAssets(res.data.assets.map(a => ({
          type: a.type, url: a.png_url, label: a.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        })));
      }
    } catch {}
  }, [orderId, headers]);

  const runValidation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/design/order/${orderId}/validate`, { headers });
      setValidationErrors(res.data.errors || []);
      setWarnings(res.data.warnings || []);
    } catch {}
  }, [orderId, headers]);

  // Run validation when entering Review step
  useEffect(() => {
    if (step === 4 && token && !loading) runValidation();
  }, [step, token, loading, runValidation]);

  // Load existing assets when session is known — placed after loadAssets definition
  useEffect(() => {
    if (sessionId && token) loadAssets();
  }, [sessionId, token, loadAssets]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDF6E9]">
        <div className="animate-spin w-8 h-8 border-4 border-[#1B3A5C] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!eligible) {
    return (
      <div className="min-h-screen bg-[#FDF6E9] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm max-w-md text-center" data-testid="design-not-eligible">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1B3A5C] mb-2">Design Builder Unavailable</h2>
          <p className="text-gray-500 mb-4">
            This order (status: <span className="font-semibold">{orderStatus}</span>) is not currently eligible for the design builder.
          </p>
          <Link to="/portal/dashboard" className="text-[#4A90A4] font-semibold hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const isLocked = !['draft', 'revisions_requested'].includes(sessionStatus);

  // Filtered icons
  const filteredIcons = icons.filter(i => {
    if (iconFilter && i.category !== iconFilter) return false;
    if (iconSearch) {
      const s = iconSearch.toLowerCase();
      return i.name.toLowerCase().includes(s) || (i.tags && i.tags.some(t => t.includes(s)));
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#FDF6E9]" data-testid="design-builder-page">
      {/* Header */}
      <div className="bg-[#1B3A5C] text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/portal/dashboard')} className="hover:bg-white/10 p-1.5 rounded-lg transition"
                    data-testid="design-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-lg">Design Builder</h1>
              <p className="text-xs text-white/60">Order #{orderId?.slice(0, 8)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sessionStatus === 'draft' && (
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Draft</span>
            )}
            {sessionStatus === 'finalized_by_customer' && (
              <span className="text-xs bg-green-500/30 text-green-200 px-2 py-1 rounded-full flex items-center gap-1">
                <Check className="w-3 h-3" /> Finalized
              </span>
            )}
            {sessionStatus === 'under_staff_review' && (
              <span className="text-xs bg-blue-500/30 text-blue-200 px-2 py-1 rounded-full flex items-center gap-1">
                <Eye className="w-3 h-3" /> Under Review
              </span>
            )}
            {sessionStatus === 'revisions_requested' && (
              <span className="text-xs bg-amber-500/30 text-amber-200 px-2 py-1 rounded-full">Revisions Requested</span>
            )}
            {sessionStatus === 'proof_ready' && (
              <span className="text-xs bg-cyan-500/30 text-cyan-200 px-2 py-1 rounded-full flex items-center gap-1">
                <Check className="w-3 h-3" /> Proof Ready
              </span>
            )}
            {sessionStatus === 'approved' && (
              <span className="text-xs bg-emerald-500/30 text-emerald-200 px-2 py-1 rounded-full flex items-center gap-1">
                <Check className="w-3 h-3" /> Approved
              </span>
            )}
            {sessionStatus === 'locked' && (
              <span className="text-xs bg-gray-500/30 text-gray-200 px-2 py-1 rounded-full flex items-center gap-1">
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Step nav */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto flex overflow-x-auto">
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap
                ${step === i ? 'border-[#4A90A4] text-[#1B3A5C]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid={`step-${s.key}`}>
              <s.icon className="w-4 h-4" />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Live Preview — sticky on desktop */}
          <div className="lg:col-span-2 lg:sticky lg:top-4 lg:self-start">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wider">Live Preview</h3>
              <div className="flex justify-center">
                <DesignPreview
                  design={design}
                  icon={selectedIcon}
                  layout={selectedLayout}
                  font={selectedFont}
                  canvasPreset={selectedCanvas}
                  size={280}
                />
              </div>
              {/* Canvas info */}
              {selectedCanvas && (
                <div className="mt-3 text-center text-xs text-gray-400">
                  Canvas: {selectedCanvas.name} ({selectedCanvas.width_inches}" x {selectedCanvas.height_inches}")
                </div>
              )}
              {/* Overflow warning */}
              {overflowDetected && !design.overflowApproved && (
                <div className="mt-3 bg-amber-50 border border-amber-300 p-3 rounded-xl" data-testid="overflow-warning">
                  <p className="text-xs text-amber-800 font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Design exceeds printable area
                  </p>
                  <p className="text-[11px] text-amber-600 mt-1">
                    Your text extends beyond the {selectedCanvas?.name} canvas. Parts may be cut off during production.
                  </p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" checked={design.overflowApproved}
                      onChange={e => updateDesign('overflowApproved', e.target.checked)}
                      className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                      data-testid="overflow-approve-checkbox" />
                    <span className="text-xs text-amber-700 font-medium">I understand my design exceeds the canvas and approve</span>
                  </label>
                </div>
              )}
              {overflowDetected && design.overflowApproved && (
                <div className="mt-3 bg-green-50 border border-green-200 p-2 rounded-lg text-xs text-green-700 flex items-center gap-1.5" data-testid="overflow-approved">
                  <Check className="w-3.5 h-3.5" /> Overflow acknowledged
                </div>
              )}
              {/* Action buttons */}
              <div className="mt-6 space-y-2">
                {!isLocked && (
                  <button onClick={handleFinalize} disabled={saving || !design.mainText || !design.layoutId || !design.canvasPresetId || (overflowDetected && !design.overflowApproved)}
                    className="w-full bg-[#1B3A5C] text-white py-2.5 rounded-xl font-semibold text-sm
                               disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#152e4a] transition"
                    data-testid="finalize-design-btn">
                    {saving ? 'Finalizing...' : 'Finalize Design'}
                  </button>
                )}
                {sessionStatus === 'finalized_by_customer' && (
                  <button onClick={handleUnlock}
                    className="w-full border border-[#1B3A5C] text-[#1B3A5C] py-2.5 rounded-xl font-semibold text-sm hover:bg-[#1B3A5C]/5 transition flex items-center justify-center gap-2"
                    data-testid="unlock-design-btn">
                    <RotateCcw className="w-4 h-4" /> Edit Design
                  </button>
                )}
              </div>
              {/* Warnings */}
              {warnings.length > 0 && (
                <div className="mt-4 space-y-2">
                  {warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Controls panel */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-6 shadow-sm min-h-[400px]" data-testid="design-controls">
              {isLocked && (
                <div className="mb-4 bg-blue-50 text-blue-700 text-sm p-3 rounded-lg flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Design is {sessionStatus.replace(/_/g, ' ')}. Editing is disabled.
                </div>
              )}

              {/* Staff revision notes */}
              {staffNotes && sessionStatus === 'revisions_requested' && (
                <div className="mb-4 bg-amber-50 border border-amber-200 p-4 rounded-xl" data-testid="staff-revision-notes">
                  <p className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Revision Requested by Staff
                  </p>
                  <p className="text-sm text-amber-700">{staffNotes}</p>
                  <p className="text-xs text-amber-500 mt-2">Please update your design and re-finalize.</p>
                </div>
              )}

              {/* Validation errors */}
              {validationErrors.length > 0 && step === 4 && (
                <div className="mb-4 bg-red-50 border border-red-200 p-3 rounded-xl" data-testid="validation-errors">
                  {validationErrors.map((e, i) => (
                    <p key={i} className="text-sm text-red-600 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {e}
                    </p>
                  ))}
                </div>
              )}

              {/* Step: Layout */}
              {step === 0 && (
                <div data-testid="step-layout-panel">
                  {/* Order Placement Selection (auto-canvas) */}
                  {orderItems.length > 0 ? (
                    <>
                      <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Design Placement</h2>
                      <p className="text-sm text-gray-400 mb-4">Select the item and placement you're designing for — canvas size is set automatically from your order.</p>
                      <div className="space-y-2 mb-8">
                        {orderItems.map((item, idx) => {
                          const opts = item.customization_options || {};
                          const decoSize = opts.decoration_size;
                          const complexity = opts.decoration_complexity;
                          const placements = opts.num_placements || 1;
                          const matched = decoSize ? _matchCanvasPreset(decoSize, canvasPresets) : null;
                          const isSelected = design.canvasPresetId === matched?.id && selectedPlacement?.item?.id === item.id;
                          return (
                            <button key={item.id || idx} disabled={isLocked}
                              onClick={() => {
                                if (matched) {
                                  updateDesign('canvasPresetId', matched.id);
                                  setSelectedPlacement({ item, size: decoSize });
                                }
                              }}
                              data-testid={`placement-item-${idx}`}
                              className={`w-full p-4 rounded-xl border-2 text-left transition
                                ${isSelected ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-gray-100 hover:border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-semibold text-sm text-[#1B3A5C]">{item.product_name}</span>
                                {isSelected && <Check className="w-4 h-4 text-[#F5A623]" />}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span>Qty: {item.quantity}</span>
                                {decoSize && <span>{decoSize}" print area</span>}
                                {matched && <span className="text-[#4A90A4] font-medium">→ {matched.name} ({matched.width_inches}" × {matched.height_inches}")</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Select Canvas Size</h2>
                      <p className="text-sm text-gray-400 mb-4">Choose the printable area for your design placement</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-8">
                        {canvasPresets.map(cp => (
                          <button key={cp.id} disabled={isLocked}
                            onClick={() => updateDesign('canvasPresetId', cp.id)}
                            className={`p-3 rounded-xl border-2 text-left transition
                              ${design.canvasPresetId === cp.id
                                ? 'border-[#F5A623] bg-[#F5A623]/5'
                                : 'border-gray-100 hover:border-gray-200'}`}
                            data-testid={`canvas-${cp.slug}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-xs text-[#1B3A5C]">{cp.name}</span>
                              {design.canvasPresetId === cp.id && <Check className="w-3.5 h-3.5 text-[#F5A623]" />}
                            </div>
                            <p className="text-lg font-bold text-[#4A90A4]">{cp.width_inches}" x {cp.height_inches}"</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{cp.description}</p>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Layout Selection */}
                  <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Choose a Layout</h2>
                  <p className="text-sm text-gray-400 mb-5">Select how your design elements are arranged</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {layouts.map(l => (
                      <button key={l.slug} disabled={isLocked}
                        onClick={() => updateDesign('layoutId', l.slug)}
                        className={`p-4 rounded-xl border-2 text-left transition
                          ${design.layoutId === l.slug
                            ? 'border-[#4A90A4] bg-[#4A90A4]/5'
                            : 'border-gray-100 hover:border-gray-200'}`}
                        data-testid={`layout-${l.slug}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-[#1B3A5C]">{l.name}</span>
                          {design.layoutId === l.slug && <Check className="w-4 h-4 text-[#4A90A4]" />}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{l.description}</p>
                        <div className="flex gap-2 mt-2">
                          {l.supports_icon && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Icon</span>}
                          <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Text</span>
                          {l.supports_subtext && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Subtext</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step: Icon */}
              {step === 1 && (
                <div data-testid="step-icon-panel">
                  <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Choose an Icon</h2>
                  <p className="text-sm text-gray-400 mb-4">Select a symbol for your design</p>
                  {/* Search + Filter */}
                  <div className="flex gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                      <input type="text" value={iconSearch} disabled={isLocked}
                        onChange={e => setIconSearch(e.target.value)}
                        placeholder="Search icons..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#4A90A4]"
                        data-testid="icon-search" />
                    </div>
                    <select value={iconFilter} disabled={isLocked}
                      onChange={e => setIconFilter(e.target.value)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#4A90A4]"
                      data-testid="icon-category-filter">
                      <option value="">All Categories</option>
                      {iconCategories.map(c => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  {/* No icon option */}
                  <button disabled={isLocked}
                    onClick={() => updateDesign('iconId', '')}
                    className={`mb-3 px-3 py-1.5 rounded-lg text-xs font-medium transition
                      ${!design.iconId ? 'bg-[#4A90A4] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    data-testid="no-icon-btn">
                    No Icon
                  </button>
                  {/* Icon grid */}
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-2 max-h-[400px] overflow-y-auto pr-1">
                    {filteredIcons.map(icon => (
                      <button key={icon.id} disabled={isLocked}
                        onClick={() => updateDesign('iconId', icon.id)}
                        className={`relative p-2 rounded-xl border-2 transition flex flex-col items-center gap-1 group
                          ${design.iconId === icon.id
                            ? 'border-[#4A90A4] bg-[#4A90A4]/5'
                            : 'border-gray-50 hover:border-gray-200'}`}
                        data-testid={`icon-${icon.name.toLowerCase().replace(/\s/g, '-')}`}
                        title={icon.name}>
                        <div className="w-10 h-10 flex items-center justify-center text-[#1B3A5C]"
                          dangerouslySetInnerHTML={{ __html: icon.svg_data.replace(/currentColor/g, '#1B3A5C') }} />
                        <span className="text-[9px] text-gray-400 truncate w-full text-center leading-tight">{icon.name}</span>
                        {design.iconId === icon.id && (
                          <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-[#4A90A4] rounded-full flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {filteredIcons.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-8">No icons match your search</p>
                  )}
                </div>
              )}

              {/* Step: Text */}
              {step === 2 && (
                <div data-testid="step-text-panel">
                  <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Enter Your Text</h2>
                  <p className="text-sm text-gray-400 mb-5">Add the text for your design</p>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-[#1B3A5C] mb-1.5">Main Text *</label>
                      <input type="text" value={design.mainText} disabled={isLocked} maxLength={40}
                        onChange={e => updateDesign('mainText', e.target.value)}
                        placeholder="e.g., NORTHWEST ACADEMY"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#4A90A4]"
                        data-testid="main-text-input" />
                      <p className="text-xs text-gray-300 mt-1">{design.mainText.length}/40 characters</p>
                    </div>
                    {selectedLayout?.supports_subtext !== false && (
                      <div>
                        <label className="block text-sm font-semibold text-[#1B3A5C] mb-1.5">Subtext (optional)</label>
                        <input type="text" value={design.subtext} disabled={isLocked} maxLength={30}
                          onChange={e => updateDesign('subtext', e.target.value)}
                          placeholder="e.g., EST. 2005"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#4A90A4]"
                          data-testid="subtext-input" />
                        <p className="text-xs text-gray-300 mt-1">{design.subtext.length}/30 characters</p>
                      </div>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={design.uppercase} disabled={isLocked}
                        onChange={e => updateDesign('uppercase', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#4A90A4] focus:ring-[#4A90A4]"
                        data-testid="uppercase-toggle" />
                      <span className="text-sm text-gray-600">UPPERCASE</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Step: Style (Font + Colors) */}
              {step === 3 && (
                <div data-testid="step-style-panel">
                  <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Style Your Design</h2>
                  <p className="text-sm text-gray-400 mb-5">Choose fonts and colors</p>

                  {/* Font selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-[#1B3A5C] mb-3">Font</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {fonts.map(f => (
                        <button key={f.id} disabled={isLocked}
                          onClick={() => updateDesign('fontId', f.id)}
                          className={`p-3 rounded-xl border-2 text-left transition
                            ${design.fontId === f.id
                              ? 'border-[#4A90A4] bg-[#4A90A4]/5'
                              : 'border-gray-100 hover:border-gray-200'}`}
                          data-testid={`font-${f.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-[#1B3A5C]"
                              style={{ fontFamily: f.family }}>{f.name}</span>
                            {design.fontId === f.id && <Check className="w-4 h-4 text-[#4A90A4]" />}
                          </div>
                          <span className="text-[10px] text-gray-400 capitalize">{f.classification.replace('_', ' ')}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color selection */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1B3A5C] mb-2">Primary Color</label>
                      <div className="flex flex-wrap gap-2">
                        {COLOR_PRESETS.map(c => (
                          <button key={`prim-${c}`} disabled={isLocked}
                            onClick={() => updateDesign('primaryColor', c)}
                            className={`w-8 h-8 rounded-full border-2 transition
                              ${design.primaryColor === c ? 'border-[#4A90A4] scale-110' : 'border-gray-200'}`}
                            style={{ backgroundColor: c }}
                            data-testid={`primary-color-${c.replace('#', '')}`} />
                        ))}
                        <input type="color" value={design.primaryColor} disabled={isLocked}
                          onChange={e => updateDesign('primaryColor', e.target.value)}
                          className="w-8 h-8 rounded-full cursor-pointer border-2 border-gray-200"
                          data-testid="primary-color-picker" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1B3A5C] mb-2">Secondary Color</label>
                      <div className="flex flex-wrap gap-2">
                        {COLOR_PRESETS.map(c => (
                          <button key={`sec-${c}`} disabled={isLocked}
                            onClick={() => updateDesign('secondaryColor', c)}
                            className={`w-8 h-8 rounded-full border-2 transition
                              ${design.secondaryColor === c ? 'border-[#4A90A4] scale-110' : 'border-gray-200'}`}
                            style={{ backgroundColor: c }}
                            data-testid={`secondary-color-${c.replace('#', '')}`} />
                        ))}
                        <input type="color" value={design.secondaryColor} disabled={isLocked}
                          onChange={e => updateDesign('secondaryColor', e.target.value)}
                          className="w-8 h-8 rounded-full cursor-pointer border-2 border-gray-200"
                          data-testid="secondary-color-picker" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step: Review */}
              {step === 4 && (
                <div data-testid="step-review-panel">
                  <h2 className="text-lg font-bold text-[#1B3A5C] mb-1">Review Your Design</h2>
                  <p className="text-sm text-gray-400 mb-5">Verify everything looks right before finalizing</p>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-sm text-gray-500">Layout</span>
                      <span className="text-sm font-semibold text-[#1B3A5C]">
                        {selectedLayout?.name || <span className="text-amber-500">Not selected</span>}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-sm text-gray-500">Icon</span>
                      <span className="text-sm font-semibold text-[#1B3A5C]">
                        {selectedIcon?.name || 'None'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-sm text-gray-500">Main Text</span>
                      <span className="text-sm font-semibold text-[#1B3A5C]">
                        {design.mainText || <span className="text-red-500">Required</span>}
                      </span>
                    </div>
                    {design.subtext && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-sm text-gray-500">Subtext</span>
                        <span className="text-sm font-semibold text-[#1B3A5C]">{design.subtext}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-sm text-gray-500">Font</span>
                      <span className="text-sm font-semibold text-[#1B3A5C]" style={{ fontFamily: selectedFont?.family }}>
                        {selectedFont?.name || 'Montserrat'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-sm text-gray-500">Colors</span>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full border border-gray-200" style={{ backgroundColor: design.primaryColor }} />
                        <div className="w-6 h-6 rounded-full border border-gray-200" style={{ backgroundColor: design.secondaryColor }} />
                      </div>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-sm text-gray-500">Uppercase</span>
                      <span className="text-sm font-semibold text-[#1B3A5C]">{design.uppercase ? 'Yes' : 'No'}</span>
                    </div>
                  </div>

                  {/* Export buttons */}
                  <div className="mt-6 space-y-2">
                    <button onClick={handleRenderPNG} disabled={rendering || !design.mainText}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#4A90A4] text-white rounded-xl font-semibold text-sm hover:bg-[#3d7a8e] disabled:opacity-40 transition"
                      data-testid="render-png-btn">
                      {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {rendering ? 'Rendering...' : 'Generate PNG Export'}
                    </button>
                    <button onClick={handleRenderMockup} disabled={rendering || !design.mainText}
                      className="w-full flex items-center justify-center gap-2 py-2.5 border border-[#4A90A4] text-[#4A90A4] rounded-xl font-semibold text-sm hover:bg-[#4A90A4]/5 disabled:opacity-40 transition"
                      data-testid="render-mockup-btn">
                      {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                      {rendering ? 'Rendering...' : 'Generate Garment Mockup'}
                    </button>
                  </div>

                  {/* Rendered assets */}
                  {assets.length > 0 && (
                    <div className="mt-6" data-testid="rendered-assets">
                      <h3 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wider">Rendered Assets</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {assets.map((asset, i) => (
                          <div key={i} className="border border-gray-100 rounded-xl overflow-hidden group">
                            <div className="aspect-square bg-gray-50 flex items-center justify-center p-2">
                              <img src={`${API}${asset.url}`} alt={asset.label}
                                className="max-w-full max-h-full object-contain" />
                            </div>
                            <div className="p-2 border-t border-gray-50 flex items-center justify-between">
                              <span className="text-xs text-gray-500">{asset.label}</span>
                              <a href={`${API}${asset.url}`} download target="_blank" rel="noreferrer"
                                className="text-xs text-[#4A90A4] hover:underline"
                                data-testid={`download-${asset.type}`}>
                                Download
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {warnings.length > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-xl">
                      <p className="text-xs font-semibold text-amber-700 mb-2">Heads up:</p>
                      {warnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-600 flex items-start gap-1.5 mb-1">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 p-3 bg-gray-50 rounded-xl text-xs text-gray-400">
                    <p>Final stitched result may vary slightly from preview. Fine details may be simplified during production.</p>
                  </div>
                </div>
              )}

              {/* Step navigation */}
              <div className="flex justify-between mt-8 pt-4 border-t border-gray-50">
                <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                  className="text-sm text-gray-400 hover:text-gray-600 disabled:opacity-30 transition"
                  data-testid="prev-step-btn">
                  Previous
                </button>
                {step < STEPS.length - 1 && (
                  <button onClick={() => setStep(step + 1)}
                    className="flex items-center gap-1 text-sm font-semibold text-[#4A90A4] hover:text-[#1B3A5C] transition"
                    data-testid="next-step-btn">
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignBuilderPage;
